import { ParsedUrlQuery } from 'querystring'
import {
    Autocomplete,
    AutocompleteRenderInputParams,
    AutocompleteRenderOptionState,
    Box,
    InputAdornment,
    InputBase,
    Link,
    Popper,
    Typography,
} from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import { useTheme } from '@mui/material/styles'
import { AutocompleteCloseReason, FilterOptionsState } from '@mui/material/useAutocomplete'
import { C1ButtonColorType, C1Button } from 'c1ui'
import clsx from 'clsx'
import { C1ButtonDropdownEndIcon } from 'components/core/button'
import { C1Icon } from 'components/core/icons/C1Icon'
import { CancelFunc } from 'lib/cancel'
import { LucideIcon, Search } from 'lucide-react'
import { useRouter } from 'next/router'
import React, { forwardRef, ReactNode, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useStyles } from './styles'

export type onChangeFunc<T> = (selected: T[], loading: boolean) => void
export type fromQueryFunc<T> = (query: ParsedUrlQuery) => T[]
export type toQueryFunc<T> = (query: ParsedUrlQuery, selected: T[]) => void

type filterProps<T> = {
    label: string
    options: T[]
    selected?: T[] | null

    fromQuery?: fromQueryFunc<T>
    toQuery?: toQueryFunc<T>
    onChange: onChangeFunc<T>
    onOptionSelected?: (event: SyntheticEvent, opts: T[]) => void
    search?: (query: string, exclude: T[]) => CancelFunc
    doFetch?: (options: T[]) => CancelFunc

    renderLabel: (option: T) => string
    renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: T) => React.ReactElement
    filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[]
    getOptionSelected?: (option1: T, option2: T) => boolean
    widePopper?: boolean
    onOpen?: () => void

    clearOnBlur?: boolean
    clearOnClick?: boolean
    loading?: boolean
    error?: boolean
    noOptionsText?: React.ReactNode
    disabled?: boolean
    errorText?: React.ReactNode
    loadingText?: React.ReactNode
    LeftIcon?: LucideIcon
    showClearButton?: boolean
    showSelectedInButton?: boolean
}

const defaultGetOptionSelected = <T, >(o1: T, o2: T): boolean => o1 === o2

export const Filter = <T, >({
    label,
    options,
    selected,
    fromQuery,
    toQuery,
    onChange,
    search,
    doFetch,
    onOptionSelected,
    renderLabel,
    renderOption,
    filterOptions,
    getOptionSelected: _getOptionSelected,
    widePopper = false,
    onOpen,
    loading = undefined,
    noOptionsText = '(No options)',
    disabled = false,
    error,
    errorText,
    loadingText,
    clearOnBlur,
    clearOnClick = true, // backwards compatability
    LeftIcon,
    showClearButton = false,
    showSelectedInButton = true,
}: filterProps<T>): React.ReactElement => {
    const router = useRouter()
    const theme = useTheme()
    const classes = useStyles(theme)

    const searchCancelRef = useRef<CancelFunc>(() => undefined)
    const fetchCancelRef = useRef<CancelFunc>(() => undefined)

    const [loaded, setLoaded] = useState<boolean>(false)
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const [inputValue, setInputValue] = useState('')
    const [pendingValue, setPendingValue] = React.useState<T[]>([])

    const getOptionSelected = useMemo(() => _getOptionSelected || defaultGetOptionSelected, [_getOptionSelected])

    // Cancel any in-flight searches on unmount
    useEffect((): (() => void) => {
        return (): void => {
            searchCancelRef.current()
            fetchCancelRef.current()
        }
    }, [])

    // Load initial state from query and fire off a fetch.
    useEffect(() => {
        if (loaded || !fromQuery) {
            return
        }
        setLoaded(true)

        const selectedFromQuery = fromQuery(router.query)
        setPendingValue(selectedFromQuery)
        if (doFetch && selectedFromQuery.length > 0) {
            fetchCancelRef.current()
            fetchCancelRef.current = doFetch(selectedFromQuery)
        }
        onChange(selectedFromQuery, true)
    }, [router.query, loaded, fromQuery, onChange, doFetch])

    // If our current selection is provided by the filter, we need to listen to it.
    useEffect(() => {
        if (selected) {
            setPendingValue(selected)
        }
    }, [selected])

    const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
        setAnchorEl(event.currentTarget)
        if (search && clearOnClick) {
            searchCancelRef.current()
            searchCancelRef.current = search('', pendingValue)
        }
    }

    const handleClose = (_event: React.ChangeEvent<unknown>, reason: AutocompleteCloseReason): void => {
        if (reason === 'toggleInput') {
            return
        }
        if (anchorEl) {
            anchorEl.focus()
        }
        setAnchorEl(null)
        toQuery && toQuery(router.query, pendingValue)
        onChange(pendingValue, false)
    }

    const open = Boolean(anchorEl)
    const id = open ? 'search-filter' : undefined

    const handleInputChange = useCallback(
        (event: SyntheticEvent, newInputValue): void => {
            if (!clearOnClick && (!event || event?.type !== 'change')) {
                return
            }
            setInputValue(newInputValue)
            if (search) {
                searchCancelRef.current()
                searchCancelRef.current = search(newInputValue, pendingValue)
            }
        },
        [search, pendingValue, clearOnClick],
    )

    const handleChange = useCallback((event: SyntheticEvent, option: null | T | T[]): void => {
        if (!option) {
            option = []
        }

        if (!Array.isArray(option)) {
            option = [option]
        }

        onOptionSelected?.(event, option)

        setPendingValue(option)
    }, [onOptionSelected])

    const renderCheckboxOption = useCallback(
        (
            props: React.HTMLAttributes<HTMLLIElement> & React.Attributes,
            option: T,
            optionState: AutocompleteRenderOptionState,
        ): React.ReactElement => {
            const getId = (): string => {
                if (option && typeof option === 'object' && 'id' in option) {
                    return option.id as string
                }
                if (typeof option === 'number') {
                    return option.toString()
                }
                return props.id as string
            }
            return (
                <li {...props} key={getId()} className={clsx(classes.item, props.className)}>
                    <Checkbox
                        color="primary"
                        size="small"
                        checked={optionState.selected}
                    />
                    {renderOption ? (
                        renderOption(props, option)
                    ) : (
                        <Typography>{renderLabel(option)}</Typography>
                    )}
                </li>
            )
        },
        [renderOption, renderLabel, classes.item],
    )

    const handleFilterOptions = useCallback(
        (opts: T[], state: FilterOptionsState<T>): T[] => {
            if (filterOptions) {
                opts = filterOptions(opts, state)
            }

            // Always show selections in the options, and put the selections at the top.
            const sortedOptions: T[] = []
            pendingValue.forEach((selection) => {
                sortedOptions.push(selection)
            })
            opts.forEach((option) => {
                const selection = sortedOptions.find((sortedOpt) => getOptionSelected(option, sortedOpt))
                if (!selection) {
                    sortedOptions.push(option)
                }
            })

            return sortedOptions
        },
        [pendingValue, filterOptions, getOptionSelected],
    )

    const buttonLabel = useMemo((): React.ReactElement => {
        let labelPrefixText = label
        if (pendingValue.length > 0) {
            labelPrefixText += ': '
        }
        const prefix = <span style={{ whiteSpace: 'pre' }}>{labelPrefixText}</span>

        let option: React.ReactElement | null = null
        if (pendingValue.length > 0) {
            option = <span>{renderLabel(pendingValue[0])}</span>
        }

        let more: React.ReactElement | null = null
        if (pendingValue.length > 1) {
            const l = pendingValue.length - 1
            more = <span style={{ whiteSpace: 'pre' }}>{` and ${l} more`}</span>
        }

        if (showSelectedInButton) {
            return <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {LeftIcon && <C1Icon icon={LeftIcon} size="md" sx={{ color: 'inherit', pr: 1 }} />}
                {prefix}
                {option}
                {more}
            </Box>
        }
        return <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {LeftIcon && <C1Icon icon={LeftIcon} size="md" sx={{ color: 'inherit', pr: 1 }} />}
            <span>{label}</span>
        </Box>
    }, [showSelectedInButton, pendingValue, label, renderLabel, LeftIcon])

    useEffect(() => {
        open && onOpen?.()
    }, [open, onOpen])

    const renderInput: React.FC<AutocompleteRenderInputParams> = useCallback(
        ({ inputProps, InputProps }): React.ReactElement => (
            <InputBase
                ref={InputProps.ref}
                inputProps={inputProps}
                autoFocus
                className={classes.inputBase}
                placeholder="Search"
                endAdornment={
                    <InputAdornment position="end">
                        <C1Icon icon={Search} size="sm" />
                    </InputAdornment>
                }
            />
        ),
        [classes.inputBase],
    )

    const CustomListbox = useMemo(
        () => forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLElement>>(
            (props, ref): React.ReactElement => (
                <Box>
                    <ul {...props} ref={ref} />
                    {showClearButton && pendingValue.length > 0 && (
                        <Box
                            sx={{
                                borderTop: 1,
                                borderColor: 'divider',
                                p: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                backgroundColor: 'background.paper',
                            }}
                            onMouseDown={(e): void => {
                                e.preventDefault()
                            }}
                        >
                            <Link
                                component="button"
                                variant="caption"
                                onClick={(e): void => {
                                    e.stopPropagation()
                                    setPendingValue([])
                                    onChange([], false)
                                }}
                                sx={{
                                    cursor: 'pointer',
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                }}
                            >
                                Clear
                            </Link>
                        </Box>
                    )}
                </Box>
            ),
        ),
        [showClearButton, pendingValue.length, onChange],
    )

    const nonErrorColor: C1ButtonColorType = pendingValue.length > 0 ? 'primary' : 'info'
    return (
        <>
            <div className={classes.root}>
                <C1Button
                    sx={{
                        borderColor: 'divider',
                    }}
                    variant="outlined"
                    color={error ? 'error' : nonErrorColor}
                    onClick={handleClick}
                    aria-describedby={id}
                    endIcon={<C1ButtonDropdownEndIcon />}
                >
                    {buttonLabel}
                </C1Button>
            </div>
            <Popper
                sx={{ zIndex: 1000 }}
                id={id}
                open={open}
                anchorEl={anchorEl}
                placement="bottom-start"
                className={clsx(classes.popper, { [classes.widePopper]: widePopper })}
                disablePortal
            >
                <Autocomplete
                    open
                    clearOnBlur={clearOnBlur}
                    disabled={disabled}
                    onClose={handleClose}
                    multiple
                    classes={{
                        paper: classes.paper,
                        option: classes.option,
                        popperDisablePortal: classes.popperDisablePortal,
                    }}
                    value={pendingValue}
                    onChange={handleChange}
                    inputValue={inputValue}
                    onInputChange={handleInputChange}
                    disableCloseOnSelect
                    filterOptions={handleFilterOptions}
                    renderTags={(): ReactNode => null}
                    loading={loading}
                    noOptionsText={error ? errorText : noOptionsText}
                    loadingText={loadingText}
                    renderOption={renderCheckboxOption}
                    options={error ? [] : [...pendingValue, ...options]}
                    getOptionLabel={renderLabel}
                    renderInput={renderInput}
                    ListboxComponent={CustomListbox}
                />
            </Popper>
        </>
    )
}
